import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAlignmentRoadmapScope } from './AlignmentRoadmapScopeContext';

const ALL_VALUE = 'all';

/**
 * Page-header roadmap scope. Sits under the Strategic Alignment title.
 * Long names fade in the closed control so the chevron stays readable;
 * the open list can grow wider than the trigger.
 */
export const AlignmentRoadmapSelect: React.FC = () => {
  const { t } = useLanguage();
  const { scope, setScope, roadmaps } = useAlignmentRoadmapScope();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedValue =
    scope.mode === 'selected' && scope.roadmapIds[0] ? scope.roadmapIds[0] : ALL_VALUE;
  const selectedLabel =
    selectedValue === ALL_VALUE
      ? t('alignment.byRoadmap.all')
      : roadmaps.find((roadmap) => roadmap.id === selectedValue)?.name || t('alignment.byRoadmap.all');

  const options = [
    { value: ALL_VALUE, label: t('alignment.byRoadmap.all') },
    ...roadmaps.map((roadmap) => ({ value: roadmap.id, label: roadmap.name }))
  ];

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const choose = (value: string) => {
    if (value === ALL_VALUE) {
      setScope({ mode: 'all', roadmapIds: [] });
    } else {
      setScope({ mode: 'selected', roadmapIds: [value] });
    }
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative w-64 max-w-full shrink-0">
      <button
        type="button"
        id="alignment-roadmap-scope"
        disabled={roadmaps.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('alignment.byRoadmap.label')}
        title={selectedLabel}
        onClick={() => setOpen((isOpen) => !isOpen)}
        className="flex w-full items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2.5 py-1.5 text-left text-sm text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] disabled:opacity-50"
      >
        <span className="relative min-w-0 flex-1 overflow-hidden whitespace-nowrap">
          {selectedLabel}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[var(--color-surface-bg)] to-transparent"
          />
        </span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-[var(--color-secondary-text)]" />
      </button>
      {open ? (
        <ul
          role="listbox"
          aria-labelledby="alignment-roadmap-scope"
          className="absolute left-0 z-50 mt-1 max-h-72 min-w-full w-max max-w-md overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] py-1 shadow-lg"
        >
          {options.map((option) => {
            const isSelected = option.value === selectedValue;
            return (
              <li key={option.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => choose(option.value)}
                  className={`block w-full px-3 py-1.5 text-left text-sm ${
                    isSelected
                      ? 'bg-[var(--color-accent-bg)]/15 text-[var(--color-accent-bg)]'
                      : 'text-[var(--color-surface-text)] hover:bg-[var(--color-accent-bg)]/10'
                  }`}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};
